import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import Menu, { menuClasses } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ArrowLeftRight } from 'lucide-react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Image3D } from '~/domain/patient/image3d';
import { ImageRegistration } from '~/domain/patient/patient';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { cutUID } from '~/utils/string';

type ImagePickerProps = {
    blendableImages: ImageRegistration[];
    onChange: (
        image: Image3D,
        registration: RigidRegistration | undefined
    ) => void;
};

export default function ImagePicker({
    blendableImages,
    onChange,
}: ImagePickerProps) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    return (
        <Box>
            <Tooltip title={<FormattedMessage id="changeImage" />}>
                <IconButton
                    color="primary"
                    size="small"
                    aria-controls="image-picker-menu"
                    aria-haspopup="true"
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={{ p: 1 }}
                >
                    <ArrowLeftRight size={12} />
                </IconButton>
            </Tooltip>
            <Menu
                id="image-picker-menu"
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                sx={{
                    width: 'calc(240px + 8px)',
                    ml: -0.5,
                    [`& .${menuClasses.paper}`]: {
                        width: '240px',
                        marginTop: '4px',
                    },
                }}
                MenuListProps={{ dense: true }}
            >
                {blendableImages?.map(({ image, registration }) => (
                    <MenuItem
                        key={image.dicomUid}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                        onClick={() => {
                            onChange(image, registration);
                            setAnchorEl(null);
                        }}
                    >
                        <Box width="100%" overflow="hidden">
                            <Typography
                                color="white"
                                fontSize={14}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                }}
                            >
                                {image.seriesMetadata.seriesDescription ??
                                    'Unknown Series'}
                            </Typography>
                            <Box display="flex" gap={0.5}>
                                <Typography
                                    variant="body2"
                                    fontSize={12}
                                    color={'textSecondary'}
                                >
                                    {image.seriesMetadata.modality}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontSize={12}
                                    color="textSecondary"
                                >
                                    - {cutUID(image.seriesUid, 4, 4)}{' '}
                                </Typography>
                            </Box>
                        </Box>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}
